import { ArrowRight, ReceiptText, Landmark, Check, ShieldCheck } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { TAX_POINTS_VAT, TAX_POINTS_CT } from "../data/content";

const TaxCard = ({ icon: Icon, title, points, testId, ctaTestId, onEnquire, service, delay }) => (
  <Reveal delay={delay} className="h-full">
    <article data-testid={testId} className="sheen-border card-lift flex h-full flex-col rounded-3xl border border-gold/20 bg-navy-850/90 p-8 sm:p-10">
      <div className="mb-6 flex items-center justify-between">
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl border border-gold/30 bg-gold/10">
          <Icon className="h-7 w-7 text-gold" />
        </span>
        <span className="rounded-full border border-gold/25 bg-navy-950/60 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-gold-soft">
          FTA-Aligned
        </span>
      </div>
      <h3 className="font-display text-2xl sm:text-3xl font-semibold text-slate-50">{title}</h3>
      <ul className="mt-7 flex-1 space-y-3.5">
        {points.map((p) => (
          <li key={p} className="flex items-start gap-3 text-sm sm:text-base text-slate-300">
            <Check className="mt-1 h-4 w-4 shrink-0 text-gold" />
            {p}
          </li>
        ))}
      </ul>
      <a
        href="#contact"
        data-testid={ctaTestId}
        onClick={() => onEnquire(service)}
        className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-gold-soft transition-colors hover:text-gold-bright"
      >
        Get Compliance Help
        <ArrowRight className="h-4 w-4" />
      </a>
    </article>
  </Reveal>
);

export const TaxSection = ({ onEnquire }) => (
  <section id="compliance" data-testid="tax-section" className="noise-overlay relative bg-navy-950 py-24 lg:py-32">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.05),transparent_60%)]" />
    <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
      <SectionHeading
        overline="VAT & Corporate Tax"
        testId="tax-heading"
        title={
          <>
            Compliance handled properly — <span className="text-gold-metallic italic">on time, every time</span>
          </>
        }
        description="UAE tax regulations demand precision. We manage your VAT and corporate tax obligations end-to-end, so deadlines never become penalties and compliance never becomes your problem."
      />

      <div className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-2">
        <TaxCard
          icon={ReceiptText}
          title="VAT Filing"
          points={TAX_POINTS_VAT}
          testId="tax-card-vat"
          ctaTestId="tax-cta-vat"
          service="VAT Filing"
          onEnquire={onEnquire}
          delay={0.1}
        />
        <TaxCard
          icon={Landmark}
          title="Corporate Tax Filing"
          points={TAX_POINTS_CT}
          testId="tax-card-corporate"
          ctaTestId="tax-cta-corporate"
          service="Corporate Tax Filing"
          onEnquire={onEnquire}
          delay={0.2}
        />
      </div>

      <Reveal delay={0.25}>
        <div className="mt-12 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 rounded-3xl border border-gold/15 bg-navy-900/70 px-8 py-6">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-5 w-5 text-gold" />
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Deadline tracking</span>
          </div>
          <span className="hidden h-1 w-1 rotate-45 bg-gold/50 sm:block" />
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Penalty protection</span>
          <span className="hidden h-1 w-1 rotate-45 bg-gold/50 sm:block" />
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Clear, quoted pricing</span>
        </div>
      </Reveal>
    </div>
  </section>
);
